/**
 * Live Trading Hedged Short Planner.
 *
 * Resolves short & protective OTM hedge candidate strikes and formats hedge plan request payloads.
 */

export interface OptionChainStrikeRow {
  strike: number;
  ce_symbol?: string;
  pe_symbol?: string;
  ce_ltp?: number;
  pe_ltp?: number;
}

export interface HedgeLegCandidate {
  symbol?: string;
  ltp?: number;
  strike: number;
}

export interface HedgeCandidatePair {
  sell: HedgeLegCandidate;
  hedge: HedgeLegCandidate;
}

export interface HedgePlanPayload {
  sell: HedgeLegCandidate;
  hedge: HedgeLegCandidate;
  lot_size: number;
  expiry: string;
  underlying_ltp?: number | null;
  utilization: number;
}

/**
 * Resolves short candidate and protective OTM hedge candidate based on hedge offset.
 */
export function resolveHedgeCandidates(
  strikes: OptionChainStrikeRow[] | null | undefined,
  selectedStrikeValue: string | number,
  side: "CE" | "PE",
  hedgeOffset: number = 6,
  liveLtp?: number,
): HedgeCandidatePair | null {
  const strikeList = Array.isArray(strikes) ? strikes : [];
  const selectedStr = String(selectedStrikeValue);
  const i = strikeList.findIndex(x => String(x.strike) === selectedStr);
  if (i < 0) return null;

  const off = hedgeOffset || 6;
  const j =
    side === "CE"
      ? Math.min(i + off, strikeList.length - 1)
      : Math.max(i - off, 0);

  if (j === i) return null;

  const srow = strikeList[i];
  const hrow = strikeList[j];

  const sellLtp = liveLtp ?? (side === "CE" ? srow.ce_ltp : srow.pe_ltp);
  const hedgeLtp = side === "CE" ? hrow.ce_ltp : hrow.pe_ltp;

  return {
    sell: {
      symbol: side === "CE" ? srow.ce_symbol : srow.pe_symbol,
      ltp: sellLtp,
      strike: srow.strike,
    },
    hedge: {
      symbol: side === "CE" ? hrow.ce_symbol : hrow.pe_symbol,
      ltp: hedgeLtp,
      strike: hrow.strike,
    },
  };
}

/**
 * Builds the request body for `/api/live/hedged-sell/plan`.
 */
export function buildHedgePlanPayload(
  candidates: HedgeCandidatePair,
  lotSize: number,
  expiry: string,
  underlyingLtp?: number | null,
  hedgeUtilPercent: number = 90,
): HedgePlanPayload {
  return {
    sell: candidates.sell,
    hedge: candidates.hedge,
    lot_size: lotSize,
    expiry,
    underlying_ltp: underlyingLtp != null ? underlyingLtp : undefined,
    utilization: (hedgeUtilPercent || 90) / 100,
  };
}
